import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Get auth token from localStorage
const getAuthToken = () => {
  return localStorage.getItem('adminToken');
};

// Create axios instance with auth header
const apiClient = axios.create({
  baseURL: API,
});

// Add auth token to requests if available
apiClient.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ==================== RECIPE API ====================

export const recipeAPI = {
  // Get all recipes
  getAll: async (category = null) => {
    try {
      const url = category ? `/recipes?category=${encodeURIComponent(category)}` : '/recipes';
      const response = await apiClient.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching recipes:', error);
      throw error;
    }
  },

  // Get single recipe
  getById: async (id) => {
    try {
      const response = await apiClient.get(`/recipes/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching recipe:', error);
      throw error;
    }
  },

  // Create recipe (admin only)
  create: async (recipeData) => {
    try {
      const response = await apiClient.post('/recipes', recipeData);
      return response.data;
    } catch (error) {
      console.error('Error creating recipe:', error);
      throw error;
    }
  },

  // Update recipe (admin only)
  update: async (id, recipeData) => {
    try {
      const response = await apiClient.put(`/recipes/${id}`, recipeData);
      return response.data;
    } catch (error) {
      console.error('Error updating recipe:', error);
      throw error;
    }
  },

  // Delete recipe (admin only)
  delete: async (id) => {
    try {
      const response = await apiClient.delete(`/recipes/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting recipe:', error);
      throw error;
    }
  },
};

// ==================== CONTACT API ====================

export const contactAPI = {
  // Submit contact form
  submit: async (contactData) => {
    try {
      const response = await apiClient.post('/contact', contactData);
      return response.data;
    } catch (error) {
      console.error('Error submitting contact form:', error);
      throw error;
    }
  },
};

// ==================== ADMIN AUTH API ====================

export const adminAPI = {
  // Admin login
  login: async (password) => {
    try {
      const response = await apiClient.post('/admin/login', { password });
      const { token, expiresIn } = response.data;
      
      // Store token in localStorage
      localStorage.setItem('adminToken', token);
      localStorage.setItem('adminTokenExpiry', Date.now() + expiresIn * 1000);
      
      return response.data;
    } catch (error) {
      console.error('Error during admin login:', error);
      throw error;
    }
  },

  // Admin logout
  logout: () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminTokenExpiry');
  },

  // Check if admin is logged in
  isLoggedIn: () => {
    const token = getAuthToken();
    const expiry = localStorage.getItem('adminTokenExpiry');
    
    if (!token || !expiry) {
      return false;
    }
    
    // Check if token is expired
    if (Date.now() > parseInt(expiry)) {
      adminAPI.logout();
      return false;
    }
    
    return true;
  },
};

export default { recipeAPI, contactAPI, adminAPI };
